/* EV Cable Hub — registration lookup.
   Wires the direction-G markup (snippets/reg-lookup-form.liquid) to the DVLA worker.

   Behaviour, spec grid and every V2L rule below are ported verbatim from the
   previous inline implementation. Do not rewrite the V2L maps from memory: they
   were researched against manufacturer sources and encode real mis-sell guards
   (a Land Rover 3-pin is a 120W inverter, not V2L).

   Worker contract, as actually implemented:
     GET <endpoint>?reg=LV71KXR      Accept: application/json
     -> { ok, isElectric, message, reg, series, make, model, year,
          connector, maxAcKw, recommendedCable,
          dcPort:{type,maxKw}, battery:{totalKwh},
          chargeTimes:[{kw,minutes}], links:[{url,label,primary}] }
*/
(function () {
  'use strict';

  var DEFAULT_ENDPOINT = 'https://evch-reg-lookup.evcablehub.workers.dev';

/* Makes we actually stock a V2L discharge lead for. Anything not listed gets an
   honest "we do not stock one" rather than a dead-end link. */
var V2L = {
  byd: 'byd-vehicle-to-load-v2l-discharge-cable',
  mg: 'mg-vehicle-to-load-v2l-discharge-cable',
  hyundai: 'hyundai-kia-vehicle-to-load-v2l-discharge-cable',
  kia: 'hyundai-kia-vehicle-to-load-v2l-discharge-cable',
  genesis: 'hyundai-kia-vehicle-to-load-v2l-discharge-cable',
  renault: 'renault-vehicle-to-load-v2l-discharge-cable',
  alpine: 'renault-vehicle-to-load-v2l-discharge-cable',
  dacia: 'renault-vehicle-to-load-v2l-discharge-cable',
  smart: 'smart-vehicle-to-load-v2l-discharge-cable',
  omoda: 'omoda-compatible-v2l-vehicle-to-load-ev-discharge-cable',
  jaecoo: 'jaecoo-compatible-v2l-vehicle-to-load-ev-discharge-cable',
  changan: 'changan-compatible-v2l-vehicle-to-load-ev-discharge-cable',
  farizon: 'farizon-compatible-v2l-vehicle-to-load-ev-discharge-cable'
};

/* Makes whose EVs DO support V2L but that we do not stock a lead for. Researched
   against manufacturer sources Aug 2026. Support is usually model-year and battery
   dependent (an ID.4 MY26 has none, MY27 does; a Corsa Electric 50kWh has none,
   the 51kWh does), so the copy never promises it for a given car. */
var V2L_OEM = {
  ford: 'Ford', volkswagen: 'Volkswagen', vw: 'Volkswagen', vauxhall: 'Vauxhall',
  opel: 'Opel', peugeot: 'Peugeot', citroen: 'Citroen', ds: 'DS', nissan: 'Nissan',
  volvo: 'Volvo', audi: 'Audi', bmw: 'BMW', skoda: 'Skoda', cupra: 'Cupra',
  jeep: 'Jeep', xpeng: 'Xpeng', leapmotor: 'Leapmotor', ldv: 'LDV', maxus: 'Maxus'
};

/* Makes with no V2L at all. Saying so plainly beats a vague maybe. */
var V2L_NONE = {
  tesla: 'Tesla', toyota: 'Toyota', lexus: 'Lexus', honda: 'Honda',
  mercedes: 'Mercedes-Benz', porsche: 'Porsche', mini: 'MINI', seat: 'SEAT',
  polestar: 'Polestar', fiat: 'Fiat', jaguar: 'Jaguar', levc: 'LEVC',
  chevrolet: 'Chevrolet', mitsubishi: 'Mitsubishi'
};

/* Cars with a built-in mains socket: they need no discharge lead at all.
   Selling one here would be the wrong product. */
var V2L_BUILTIN = { mazda: 'Mazda MX-30 R-EV' };

/* The worst mis-sell risk we found: the 3-pin socket in a Defender, Discovery or
   Range Rover is a 120W inverter outlet, NOT vehicle-to-load. */
var V2L_TRAP = { land: 'Land Rover', range: 'Range Rover' };
var CTA = { cable: 'Find my cable', v2l: 'Find my V2L lead', portable: 'Find my portable charger' };
  /* The worker only ever returns charging-cable links, so V2L and portable are
     resolved here from the make it identified. */
  function linksFor(d, purpose) {
    var make = String(d.make || '').toLowerCase().split(/[^a-z]/)[0];
    if (purpose === 'v2l') {
      var handle = V2L[make];
      if (handle) {
        return {
          note: 'Vehicle-to-load runs mains appliances from the car battery, so your '
                + esc(d.make) + ' needs a discharge lead rather than a charging cable. '
                + '<strong>Check your car actually has V2L before ordering</strong>. On several models it is '
                + 'a trim level or an optional pack rather than standard. The product page lists the models it fits.',
          html: '<a class="btn btn-dark" href="/products/' + handle + '">V2L discharge lead for '
                + esc(d.make) + '</a><a class="btn" href="/collections/v2l-adapters">All V2L leads</a>'
        };
      }
      if (V2L_TRAP[make]) {
        return {
          note: 'Careful here. The 3-pin socket in a ' + esc(V2L_TRAP[make]) + ' is a 120W inverter '
                + 'outlet for small devices, not vehicle-to-load, and no discharge lead will change that. '
                + 'A V2L lead would not work on your car, so we will not sell you one.',
          html: '<a class="btn" href="/collections/ev-charging-cables">Charging cables for your car</a>'
                + '<a class="btn" href="/pages/contact">Ask us</a>'
        };
      }
      if (V2L_BUILTIN[make]) {
        return {
          note: 'Some ' + esc(d.make) + ' models provide mains power from a socket built into the car, '
                + 'so they need no discharge lead at all. Check your handbook before buying anything.',
          html: '<a class="btn" href="/pages/contact">Ask us to check your model</a>'
        };
      }
      if (V2L_NONE[make]) {
        return {
          note: esc(V2L_NONE[make]) + ' does not currently offer vehicle-to-load on its UK cars, '
                + 'so a discharge lead has nothing to connect to. If you think your model is an '
                + 'exception, tell us and we will check it properly.',
          html: '<a class="btn" href="/collections/ev-charging-cables">Charging cables for your car</a>'
                + '<a class="btn" href="/pages/contact">Ask us</a>'
        };
      }
      if (V2L_OEM[make]) {
        return {
          note: 'Some ' + esc(V2L_OEM[make]) + ' electric models do support vehicle-to-load, but it '
                + 'depends on the exact model, battery and build year, and it needs '
                + esc(V2L_OEM[make]) + '’s own adapter from a dealer. We do not stock a lead that fits '
                + esc(V2L_OEM[make]) + ', so we will not sell you one that may not work. Tell us your model '
                + 'and we will confirm what your car actually supports.',
          html: '<a class="btn" href="/pages/contact">Ask us to check your model</a>'
                + '<a class="btn" href="/collections/v2l-adapters">See which makes we cover</a>'
        };
      }
      return {
        note: 'We do not currently stock a V2L discharge lead for ' + esc(d.make || 'this make')
              + ', and not every EV supports vehicle-to-load. Check your handbook, or ask us and we will confirm.',
        html: '<a class="btn" href="/collections/v2l-adapters">Browse V2L leads</a>'
              + '<a class="btn" href="/pages/contact">Ask us about your car</a>'
      };
    }
    if (purpose === 'portable') {
      return {
        note: 'A portable (Mode 2) charger plugs into a standard 3-pin socket at about 2.3kW. '
              + 'It is a backup for occasional use rather than a daily charger.',
        html: '<a class="btn btn-dark" href="/collections/granny-chargers">Portable chargers for '
              + esc(d.make || 'your car') + '</a>'
              + '<a class="btn" href="/collections/ev-charging-cables">Compare with a Mode 3 cable</a>'
      };
    }
    return {
      note: '',
      html: (d.links || []).map(function (l) {
        return '<a class="' + (l.primary ? 'btn btn-dark' : 'btn') + '" href="' + esc(l.url) + '">' + esc(l.label) + '</a>';
      }).join('')
    };
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function specRows(d, purpose) {
    var spec = [];
    spec.push(['Charge port', d.connector]);
    if (d.maxAcKw) spec.push(['Max AC charging', d.maxAcKw + 'kW']);
    if (purpose === 'cable') spec.push(['Cable you need', d.recommendedCable]);
    if (d.dcPort) spec.push(['Rapid charging', d.dcPort.type + ' up to ' + d.dcPort.maxKw + 'kW']);
    if (d.battery && d.battery.totalKwh) spec.push(['Battery', d.battery.totalKwh + 'kWh']);
    if (purpose === 'cable' && d.chargeTimes && d.chargeTimes.length) {
      var t = d.chargeTimes.map(function (c) {
        var h = Math.floor(c.minutes / 60), m = c.minutes % 60;
        return c.kw + 'kW: ' + (h ? h + 'h ' : '') + m + 'm';
      }).join(' \u00b7 ');
      spec.push(['Charge time 10-80%', t]);
    }
    return spec.filter(function (r) { return r[1]; });
  }

  function init(form) {
    if (form.hasAttribute('data-rl-ready')) return;
    form.setAttribute('data-rl-ready', '1');

    var endpoint = form.getAttribute('data-endpoint') || DEFAULT_ENDPOINT;
    var purpose  = form.getAttribute('data-default-purpose') || 'cable';
    var input    = form.querySelector('.plate-input');
    var result   = form.querySelector('[data-reg-result]');
    var submit   = form.querySelector('.reg-lookup-submit');
    var modes    = form.querySelectorAll('[data-mode]');
    var last     = null;

    if (!input || !result) return;

    function setCta() {
      if (!submit) return;
      var sub = submit.querySelector('.reg-lookup-sub');
      submit.firstChild.nodeValue = (CTA[purpose] || CTA.cable) + ' ';
      if (sub) submit.appendChild(sub);
    }

    function say(msg, isError) {
      result.hidden = !msg;
      result.className = 'reg-lookup-result' + (isError ? ' reg-result-notev' : '');
      result.innerHTML = msg
        ? (isError ? '<p class="reg-result-head"><span class="dot" aria-hidden="true"></span>No match</p>' : '')
          + '<p class="reg-result-car">' + esc(msg) + '</p>'
        : '';
    }

    /* The mode is chosen AFTER the reg in this direction, so the switcher is
       rendered into the result rather than above the plate. The V2L and portable
       rules below have been live in this file since the direction-G build but
       nothing ever set `purpose` away from 'cable', so they were unreachable.
       render() rewrites innerHTML, so this is delegated once rather than rebound. */
    result.addEventListener('click', function (e) {
      var b = e.target && e.target.closest ? e.target.closest('[data-mode]') : null;
      if (!b || !result.contains(b)) return;
      purpose = b.getAttribute('data-mode');
      setCta();
      if (last) render(last);
    });

    Array.prototype.forEach.call(modes, function (b) {
      b.addEventListener('click', function () {
        purpose = b.getAttribute('data-mode');
        Array.prototype.forEach.call(modes, function (o) {
          var on = o === b;
          o.classList.toggle('is-selected', on);
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        setCta();
        if (last) render(last);
      });
    });
    setCta();

    input.addEventListener('input', function () {
      var v = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (/^[A-Z]{2}[0-9]{2}[A-Z]{0,3}$/.test(v) && v.length > 4) v = v.slice(0, 4) + ' ' + v.slice(4);
      input.value = v;
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var reg = input.value.replace(/\s+/g, '');
      if (reg.length < 2) { say('Enter your registration to continue.', true); return; }
      if (!endpoint) { say('Lookup is not configured yet.', true); return; }
      if (submit) submit.disabled = true;
      say('Looking up ' + input.value.trim() + '\u2026', false);
      fetch(endpoint + '?reg=' + encodeURIComponent(reg), { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (submit) submit.disabled = false;
          var d = res.body || {};
          if (!d.ok) { say(d.message || 'We could not look that up. Please try again.', true); return; }
          /* Not an EV: refuse plainly, keep the input so another plate can be tried. */
          if (d.isElectric === false) { say(d.message || 'That does not look like an electric vehicle.', true); return; }
          last = d;
          render(d);
        })
        .catch(function () {
          if (submit) submit.disabled = false;
          say('Something went wrong. Please try again.', true);
        });
    });

    var MODE_LABEL = { cable: 'Charging cable', v2l: 'Vehicle-to-Load', portable: 'Portable charger' };

    function modeSwitcher(current) {
      var keys = ['cable', 'v2l', 'portable'];
      return '<div class="reg-modes reg-result-modes" role="radiogroup"'
        + ' aria-label="What are you looking for?">'
        + keys.map(function (k) {
            var on = k === current;
            return '<button type="button" class="reg-mode' + (on ? ' is-selected' : '') + '"'
              + ' role="radio" aria-checked="' + (on ? 'true' : 'false') + '"'
              + ' data-mode="' + k + '">' + MODE_LABEL[k] + '</button>';
          }).join('')
        + '</div>';
    }

    function render(d) {
      var L = linksFor(d, purpose);
      var name = [d.year, d.make, d.model].filter(Boolean).join(' ');
      var sub  = 'Registration ' + (d.reg || '') + (d.series ? ' \u00b7 ' + d.series : '');
      var rows = specRows(d, purpose).map(function (row) {
        return '<div class="reg-spec-row"><span>' + esc(row[0]) + '</span><strong>' + esc(row[1]) + '</strong></div>';
      }).join('');

      /* Exact match resolves to a product; otherwise we only got them to a collection. */
      var exact = /\/products\//.test(L.html);
      var state = exact ? 'reg-result-match' : 'reg-result-fallback';
      var label = exact ? 'Exact match' : 'Closest range';

      result.className = 'reg-lookup-result ' + state;
      result.hidden = false;
      result.innerHTML =
        '<p class="reg-result-head"><span class="dot" aria-hidden="true"></span>' + label + '</p>' +
        '<p class="reg-result-car">' + esc(name) + '<small>' + esc(sub) + '</small></p>' +
        (rows ? '<div class="reg-spec">' + rows + '</div>' : '') +
        (L.note ? '<p class="reg-result-note">' + L.note + '</p>' : '') +
        modeSwitcher(purpose) +
        '<div class="reg-result-actions">' + L.html + '</div>';
      result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function boot() {
    var forms = document.querySelectorAll('[data-reg-lookup]');
    Array.prototype.forEach.call(forms, init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
